import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  MailOutlined,
} from "@ant-design/icons";
import { Button, Form, Image, Input, Typography } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { IoMdPersonAdd } from "react-icons/io";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import loginImage from "../../assets/img/login-image.png";
import resolvedLogo from "../../assets/img/pickpacknewlogo.svg";
import techPackIcon from "../../assets/img/TechPack.svg";
import useStore from "../../store/useStore";
import { PASSWORD_HINT, passwordRules } from "../../utils/formRules";

import { Key, Phone, User } from "lucide-react";
import "./style.less";

const { Title, Text } = Typography;
const currentYear = new Date().getFullYear();

const steps = {
  register: "register",
  confirm: "confirm",
};

const Login = () => {
  const location = useLocation();

  const paramStep = location?.state?.step ?? "register";

  const [loading, setLoading] = useState(false);
  const [, setStep] = useState(steps.register);
  const [form] = Form.useForm();
  const history = useHistory();

  const setUser = useStore((state) => state.setUser);

  useEffect(() => {
    setStep(paramStep);
  }, [paramStep]);

  const onFinish = async (values) => {
    setLoading(true);

    const { email, password, phoneNumber, firstName, lastName } = values;

    try {
      const { data } = await axios.post(`/auth/register`, {
        email,
        password,
        phoneNumber,
        firstName,
        lastName,
      });

      if (data?.result?.status === "verify-email") {
        setUser({
          token: data?.result?.token ?? null,
          verified: false,
          codeVerified: data?.result?.codeVerified,
          email: data?.result?.email ?? null,
        });
      }
      setLoading(false);
    } catch (e) {
      console.error("Create Merchant User: ", e);
      setLoading(false);
    }
  };

  return (
    <div className="fp-wrapper">
      {/* Left — cover image */}
      <div className="fp-cover">
        <div
          className="fp-cover-bg"
          style={{ backgroundImage: `url(${loginImage})` }}
        />
        <div className="fp-cover-overlay" />
      </div>

      {/* Right — form card */}
      <div className="fp-form-side">
        <div className="fp-form-main">
          <div className="fp-form-card">
            {/* Logo */}
            <div className="fp-logo-wrap">
              <Image
                src={resolvedLogo}
                className="fp-logo"
                alt="logo"
                preview={false}
              />
            </div>

            {/* Heading */}
            <div className="fp-heading">
              <Title level={3} className="fp-title">
                Бүртгэл үүсгэх!
              </Title>
              <Text className="fp-subtitle">
                Та шинээр бүртгэл үүсгэн нэвтрэнэ үү.
              </Text>
            </div>

            {/* Form */}
            <Form
              form={form}
              name="normal_login"
              layout="vertical"
              className="fp-form"
              labelAlign="left"
              initialValues={{ remember: true }}
              onFinish={onFinish}
            >
              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Имэйл хаягаа оруулна уу!",
                  },
                  {
                    type: "email",
                    message: "Имэйл хаягийн формат буруу байна.",
                  },
                ]}
                hasFeedback
              >
                <Input
                  size="large"
                  prefix={
                    <MailOutlined size={16} style={{ color: "#626F86" }} />
                  }
                  placeholder="Имэйл хаяг"
                />
              </Form.Item>

              <Form.Item
                name="lastName"
                rules={[{ required: true, message: "Овог оруулна уу!" }]}
              >
                <Input
                  size="large"
                  prefix={<User size={16} style={{ color: "#626F86" }} />}
                  placeholder="Овог"
                />
              </Form.Item>

              <Form.Item
                name="firstName"
                rules={[{ required: true, message: "Нэр оруулна уу!" }]}
              >
                <Input
                  size="large"
                  prefix={<User size={16} style={{ color: "#626F86" }} />}
                  placeholder="Нэр"
                />
              </Form.Item>
              <Form.Item
                name="phoneNumber"
                rules={[
                  { required: true, message: "Утасны дугаараа оруулна уу!" },
                ]}
              >
                <Input
                  size="large"
                  prefix={<Phone size={16} style={{ color: "#626F86" }} />}
                  placeholder="Утасны дугаар"
                />
              </Form.Item>
              <Form.Item name="password" rules={passwordRules()} extra={PASSWORD_HINT}>
                <Input.Password
                  size="large"
                  prefix={<Key size={16} style={{ color: "#626F86" }} />}
                  placeholder="Нууц үг"
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>

              <Form.Item
                name="passwordRepeat"
                rules={[
                  {
                    required: true,
                    message: "Нууц үгээ давтаж оруулна уу!",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Нууц үг тохирохгүй байна!"),
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  size="large"
                  prefix={<Key size={16} style={{ color: "#626F86" }} />}
                  placeholder="Нууц үг давтах"
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                <Button
                  type="primary"
                  size="large"
                  className="fp-submit-btn"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  icon={<IoMdPersonAdd size={16} />}
                  htmlType="submit"
                  loading={loading}
                  block
                >
                  Бүртгүүлэх
                </Button>
              </Form.Item>
              <div className="fp-back-link">
                <Button
                  type="link"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => history.push("/")}
                  className="fp-back-btn"
                >
                  Нэвтрэх хуудас руу буцах
                </Button>
              </div>
            </Form>
          </div>
        </div>
        {/* Footer */}
        <div className="fp-footer">
          <div className="fp-footer-brand">
            <span className="fp-powered">Powered by</span>
            <Image
              src={techPackIcon}
              preview={false}
              className="fp-techpack-logo"
              alt="TechPack"
            />
          </div>
          <span className="fp-copyright">
            © 2021-{currentYear} Зохиогчийн эрх хамгаалагдсан
          </span>
        </div>
      </div>
    </div>
  );
};

export default Login;
